import { Box, Stack } from '@mui/material';
import { TableData } from 'app/concierge/types';
import MITable from 'components/common/grid/MITable';
import { ColumnType, GridData, GridDataRow } from 'components/common/grid';
import { useEffect, useMemo } from 'react';
import ExpandButton from './expand-button';

interface IProps {
    tableData: TableData;
    messageId?: string;
    conciergeResponse: React.ReactNode | null;
    isStandalone?: boolean;
}

export default function JsonTable({ tableData, messageId, conciergeResponse, isStandalone }: IProps) {
    const { data, columns: declaredColumns } = tableData;

    if (!data || data.length === 0) {
        return null;
    }

    const gridColumns = useMemo(() => {
        const columns: ColumnType[] = [];
        const titleOf = (text: string) =>
            text
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

        // Row 2120: a table that carries its columns (a parsed markdown table) shows one column per entry, in order, keyed by
        // position, so columns sharing a header each keep their own; a table without the list shows its row keys as before. A list
        // of another shape (a tool's JSON table on the MCP road) is used only when every entry names, by a string key, a cell the
        // rows hold (-8d7's S1), else the row keys stand.
        const usable =
            Array.isArray(declaredColumns) &&
            declaredColumns.length > 0 &&
            declaredColumns.every(
                (column) =>
                    typeof column?.key === 'string' &&
                    typeof column?.label === 'string' &&
                    data.some((row) => Object.prototype.hasOwnProperty.call(row, column.key)),
            );
        if (usable) {
            declaredColumns.forEach((column) => {
                columns.push({
                    name: column.key,
                    title: titleOf(column.label),
                    sortable: false,
                });
            });
        } else if (data.length > 0) {
            const firstRow = data[0];
            Object.keys(firstRow).forEach((key) => {
                columns.push({
                    name: key,
                    title: titleOf(key),
                    sortable: false,
                });
            });
        }

        return columns;
    }, [data, declaredColumns]);

    const gridData = useMemo(() => {
        const res: GridData = [];

        data.forEach((row) => {
            const rowData: GridDataRow = {};
            Object.keys(row).forEach((key) => {
                const value = row[key];

                rowData[key] = {
                    component: 'copyText',
                    config: {
                        text: String(value),
                        value: String(value),
                    },
                };
            });
            res.push(rowData);
        });

        return res;
    }, [data]);

    useEffect(() => {
        if (isStandalone) {
            const timer = setTimeout(() => {
                document.body.classList.add('component-ready');
            }, 200);

            return () => {
                clearTimeout(timer);
            };
        }
    }, []);

    if (isStandalone) {
        return (
            <MITable
                gridName={`concierge-table-${messageId}`}
                columns={gridColumns}
                massActionsConfig={{ enabled: false }}
                data={gridData}
                defaultRowHeight={28}
                autoHeight
            />
        );
    }

    return (
        <>
            <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ width: '100%', mt: 1, '.rs-table': { pb: '2px !important' } }}>
                    <MITable
                        gridName={`concierge-table-${messageId}`}
                        columns={gridColumns}
                        massActionsConfig={{
                            enabled: false,
                        }}
                        data={gridData}
                        defaultRowHeight={28}
                        autoHeight={gridData.length < 12}
                        height={gridData.length > 12 ? 300 : undefined}
                    />
                </Box>
                <ExpandButton
                    conciergeMessage={conciergeResponse}
                    content={
                        <Box sx={{ width: '100%' }}>
                            <MITable
                                gridName={`concierge-table-${messageId}`}
                                columns={gridColumns}
                                massActionsConfig={{
                                    enabled: false,
                                }}
                                data={gridData}
                                defaultRowHeight={28}
                                autoHeight={gridData.length < 20}
                                height={gridData.length >= 20 ? 600 : undefined}
                            />
                        </Box>
                    }
                />
            </Stack>
        </>
    );
}
